import { useEffect, useMemo, useState } from 'react';
import { useSession } from '../../lib/auth/session.js';
import { useCdrs, useCdrSummary, useExtensions, useTenant, cdrQuery, type CdrFilter } from '../../lib/api/hooks.js';
import { getAccessToken } from '../../lib/api/client.js';
import { toastError } from '../../components/ui/Toast.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, SearchInput, SectionHead, StatGroup, Stat, Segmented } from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';
import { Icon, type IconName } from '../../components/ui/Icon.js';
import { formatDuration, formatMoney, formatDateTime, formatNumber, timezoneAbbr } from '../../lib/format.js';
import { shownDirection, refusalText } from '@arcnet/contracts';
import { CallModal, readableNumber } from './CallDetail.js';
import { CallWorkModal } from '../agent/CallWorkModal.js';
import { Outcome, TagList } from '../agent/AgentWorkspace.js';
import { DialButton } from '../agent/CallBar.js';

/**
 * Direction as a glyph with its word beside it for assistive tech.
 *
 * Read by shape down a column, which three same-length words in one colour do
 * not allow. The tone is kept from the old badge so nobody relearns the colours.
 */
const DIRECTION: Record<string, { icon: IconName; label: string; tone: string }> = {
  inbound: { icon: 'callIn', label: 'Inbound', tone: 'var(--arc-dir-in)' },
  outbound: { icon: 'callOut', label: 'Outbound', tone: 'var(--arc-dir-out)' },
  internal: { icon: 'callInternal', label: 'Internal', tone: 'var(--arc-fg-4)' },
};

/**
 * Periods, as days in the account's timezone. Africa/Johannesburg has no daylight saving, so its
 * day starts at +02:00 all year; the list is shown in that timezone already.
 */
type Period = 'today' | 'yesterday' | '7d' | '30d' | 'month' | 'last_month' | 'custom';
const PERIODS: Array<readonly [Exclude<Period, 'custom'>, string]> = [
  ['today', 'Today'], ['yesterday', 'Yesterday'], ['7d', '7 days'], ['30d', '30 days'],
  ['month', 'This month'], ['last_month', 'Last month'],
];
const OFFSET = '+02:00';

const dayIn = (tz: string, d = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
const addDays = (day: string, n: number): string => {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const startOf = (day: string): string => `${day}T00:00:00${OFFSET}`;

/** The window a period covers: from its first day, up to the day after its last. */
function windowOf(p: Period, tz: string, custom: { from: string; to: string }): { from: string; to: string } | null {
  const today = dayIn(tz);
  const first = `${today.slice(0, 8)}01`;
  switch (p) {
    case 'today': return { from: startOf(today), to: startOf(addDays(today, 1)) };
    case 'yesterday': return { from: startOf(addDays(today, -1)), to: startOf(today) };
    case '7d': return { from: startOf(addDays(today, -6)), to: startOf(addDays(today, 1)) };
    case '30d': return { from: startOf(addDays(today, -29)), to: startOf(addDays(today, 1)) };
    case 'month': return { from: startOf(first), to: startOf(addDays(today, 1)) };
    case 'last_month': {
      const prev = `${addDays(first, -1).slice(0, 8)}01`;
      return { from: startOf(prev), to: startOf(first) };
    }
    case 'custom':
      if (!custom.from || !custom.to || custom.to < custom.from) return null;
      return { from: startOf(custom.from), to: startOf(addDays(custom.to, 1)) };
  }
}

export function CdrsPage({ tenantId: propTenantId, embedded }: {
  tenantId?: string;
  embedded?: boolean;
} = {}) {
  const activeTenantId = useSession((s) => s.activeTenantId);
  const can = useSession((s) => s.can);
  const tenantId = propTenantId ?? activeTenantId;
  // Tenant timezone, never the browser's -- a call at 09:00 in Windhoek must
  // not read as 10:00 because the admin is in Johannesburg (doc 07 §3). The account's own now
  // (UX review, 21 October 2026); the day boundaries still assume +02:00, as every account is in SA.
  const tz = useTenant(tenantId ?? null).data?.timezone || 'Africa/Johannesburg';

  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [period, setPeriod] = useState<Period>('7d');
  const [custom, setCustom] = useState(() => ({ from: addDays(dayIn(tz), -6), to: dayIn(tz) }));
  const [direction, setDirection] = useState('');
  const [result, setResult] = useState<'' | 'answered' | 'missed'>('');
  const [recorded, setRecorded] = useState(false);
  const [extensionId, setExtensionId] = useState('');
  // The call open in its window (CallDetail.tsx), if any.
  const [open, setOpen] = useState<string | null>(null);
  const [work, setWork] = useState<string | null>(null);

  // Searched as typing pauses, not on every key.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const extensions = useExtensions(can('extension:read') && tenantId ? tenantId : null, { limit: 200 });
  const win = windowOf(period, tz, custom);
  const filter: CdrFilter = useMemo(() => ({
    q: q.length >= 2 ? q : undefined,
    direction: direction || undefined,
    answered: result === 'answered' ? true : result === 'missed' ? false : undefined,
    recorded: recorded || undefined,
    extension_id: extensionId || undefined,
    from: win?.from, to: win?.to,
  }), [q, direction, result, recorded, extensionId, win?.from, win?.to]);

  const calls = useCdrs(win ? tenantId : null, filter);
  const summary = useCdrSummary(win ? tenantId : null, filter);
  /** The dates the period covers, shown beside it and editable (a change makes it Custom). */
  const shown = win ? { from: win.from.slice(0, 10), to: addDays(win.to.slice(0, 10), -1) } : custom;
  const setDates = (next: { from: string; to: string }) => { setCustom(next); setPeriod('custom'); };
  const [exporting, setExporting] = useState(false);
  /** Every call the filter matches, as a spreadsheet, not only the pages loaded. */
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await fetch(`/api/v1/tenants/${tenantId}/cdrs/export.csv${cdrQuery(filter)}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) {
        const problem = await res.json().catch(() => null) as { detail?: string; title?: string } | null;
        throw new Error(problem?.detail ?? problem?.title ?? `Export failed (${res.status})`);
      }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url; a.download = `calls-${shown.from}-to-${shown.to}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toastError(err); } finally { setExporting(false); }
  };
  const rows = calls.data?.pages.flatMap((p) => p.data) ?? [];
  const narrowed = !!(q || direction || result || recorded || extensionId || period !== '7d');
  const clear = () => {
    setSearch(''); setQ(''); setDirection(''); setResult(''); setRecorded(false); setExtensionId(''); setPeriod('7d');
  };

  if (!tenantId) {
    return <Empty icon="accounts" title="No account selected" hint="Choose an account to see its call history." />;
  }

  return (
    <>
      <SectionHead title="Call history" sub={`Times in ${timezoneAbbr(tz)} (${tz}).`}
        action={win && (
          <button type="button" className="arc-btn arc-btn-sm" disabled={exporting} onClick={() => void exportCsv()}>
            <Icon name="download" size={13} /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        )} />

      {/* One period bar: presets, and the dates they cover, always visible and editable. */}
      <div className="arc-period-bar">
        <Segmented label="Period" value={period === 'custom' ? ('' as never) : period}
          options={PERIODS} onChange={(v) => setPeriod(v)} />
        <span className="arc-row" style={{ gap: 6 }}>
          <input type="date" className="arc-input arc-date" aria-label="From" value={shown.from}
            max={shown.to || undefined} onChange={(e) => setDates({ from: e.target.value, to: shown.to })} />
          <span className="arc-subtle">to</span>
          <input type="date" className="arc-input arc-date" aria-label="To" value={shown.to}
            min={shown.from || undefined} max={dayIn(tz)}
            onChange={(e) => setDates({ from: shown.from, to: e.target.value })} />
        </span>
      </div>

      {/* The filtered calls in figures; "Not answered" applies itself as a filter. */}
      {win && (
        <div style={{ marginBottom: 14 }}>
          <StatGroup>
            <Stat bare label="Calls" value={summary.data ? formatNumber(summary.data.total_calls) : '—'} />
            <Stat bare label="Answered" value={summary.data ? `${Math.round(summary.data.answer_rate_pct)}%` : '—'}
              sub={summary.data ? `${formatNumber(summary.data.answered_calls)} calls` : undefined} />
            <Stat bare label="Not answered" value={summary.data ? formatNumber(summary.data.missed_calls) : '—'}
              sub={summary.data && summary.data.missed_calls > 0 && result !== 'missed'
                ? <button type="button" className="arc-link-btn" onClick={() => setResult('missed')}>Show them</button>
                : undefined} />
            <Stat bare label="Talk time" value={summary.data ? formatDuration(summary.data.talk_time_sec) : '—'} />
            <Stat bare label="Cost" value={summary.data ? formatMoney(summary.data.spend, summary.data.currency) : '—'}
              sub="excl. VAT" />
          </StatGroup>
        </div>
      )}

      {calls.error && <ErrorBanner error={calls.error} />}

      <div className="arc-card">
        <div className="arc-toolbar">
          <SearchInput value={search} onChange={setSearch} label="Search calls"
            placeholder="Search number, name or extension" />
          <select className="arc-select" style={{ width: 'auto' }} value={direction}
            onChange={(e) => setDirection(e.target.value)} aria-label="Direction">
            <option value="">All directions</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
            <option value="internal">Internal</option>
          </select>
          <select className="arc-select" style={{ width: 'auto' }} value={result}
            onChange={(e) => setResult(e.target.value as typeof result)} aria-label="Result">
            <option value="">Answered or not</option>
            <option value="answered">Answered</option>
            <option value="missed">Not answered</option>
          </select>
          {(extensions.data?.data.length ?? 0) > 0 && (
            <select className="arc-select" style={{ width: 'auto', maxWidth: 200 }} value={extensionId}
              onChange={(e) => setExtensionId(e.target.value)} aria-label="Extension">
              <option value="">All extensions</option>
              {extensions.data!.data.map((x) => (
                <option key={x.id} value={x.id}>{x.number}{x.display_name ? ` · ${x.display_name}` : ''}</option>
              ))}
            </select>
          )}
          <Switch id="cdr-recorded" label="Recorded only" checked={recorded} onChange={setRecorded} />
          <div className="arc-spacer" />
          {narrowed && (
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={clear}>Clear filters</button>
          )}
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Started</th>
                <th aria-label="Direction" style={{ width: 36 }} />
                <th>From</th>
                <th>To</th>
                <th className="arc-td-right">Duration</th>
                <th>Outcome</th>
                <th>Quality</th>
                <th className="arc-td-right">Cost</th>
                {/* Recordings have no page of their own: a recording is reached from its call. */}
                <th aria-label="Recording" style={{ width: 36 }} />
                <th aria-label="Notes, tags and outcome" style={{ width: 40 }} />
              </tr>
            </thead>
            {calls.data === undefined ? (
              <TableSkeleton query={calls} cols={10} />
            ) : (
              <tbody>
                {rows.map((c) => {
                  // A refused call by what was dialled (contracts shownDirection), and said to be blocked.
                  const d = DIRECTION[shownDirection(c)] ?? DIRECTION['internal']!;
                  const blocked = c.refused ? refusalText(c.refused) : null;
                  const fromName = c.from_name && !/^[\d+\s]+$/.test(c.from_name)
                    && !/^\d{2,6}-[a-z0-9.]+$/i.test(c.from_name) ? c.from_name : null;
                  return (
                    // The whole row opens the call; Enter does the same from the keyboard.
                    <tr key={c.id} onClick={() => setOpen(c.id)} style={{ cursor: 'pointer' }}
                      tabIndex={0} aria-label={`Open the call from ${readableNumber(c.from_number)}`}
                      onKeyDown={(e) => { if (e.key === 'Enter') setOpen(c.id); }}>
                      <td className="arc-num arc-subtle" style={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(c.started_at, tz)}
                      </td>
                      <td title={blocked ? `${d.label}, blocked: ${blocked}` : d.label}>
                        <span style={{ color: d.tone, display: 'inline-flex' }}>
                          <Icon name={d.icon} size={15} />
                        </span>
                        <span className="arc-sr-only">{d.label}</span>
                      </td>
                      <td>
                        {/* One of the account's own extensions: its name, whichever registration
                            placed the call (a desk phone or the app). The number is in the tooltip. */}
                        {c.from_extension
                          ? <span title={`Extension ${c.from_extension.number}`}>{c.from_extension.name}</span>
                          : <>
                              <span className="arc-num">{readableNumber(c.from_number)}</span>
                              {fromName && <span className="arc-subtle"> · {fromName}</span>}
                              <DialButton number={c.from_number} label={fromName ?? undefined} />
                            </>}
                      </td>
                      <td className={c.to_extension ? undefined : 'arc-num'}>
                        {c.to_extension
                          ? <span title={`Extension ${c.to_extension.number}`}>{c.to_extension.name}</span>
                          : <>{readableNumber(c.to_number ?? c.destination_e164)}<DialButton number={c.to_number ?? c.destination_e164} /></>}
                      </td>
                      <td className="arc-td-right arc-num">
                        {/* Answered by the switch to say why, so "answered" says nothing here. */}
                        {blocked ? <span className="arc-subtle" title={blocked}>blocked</span>
                          : c.answered_at ? formatDuration(c.duration_sec) : <span className="arc-subtle">no answer</span>}
                      </td>
                      {/* What the agent said it was (0108), in a column of its own (owner, 3 October 2026). */}
                      <td>
                        <Outcome d={c.disposition} />
                        {c.tags && c.tags.length > 0 && <div style={{ marginTop: 3 }}><TagList tags={c.tags} /></div>}
                      </td>
                      {/* Only fair or poor: good is what every call should be, so it is not said. */}
                      <td>{c.mos ? <MosIndicator mos={c.mos} /> : null}</td>
                      <td className="arc-td-right arc-num">
                        {/* Over the client's own trunk nothing is charged; their carrier's cost shows instead (0131). */}
                        {c.sell_amount === null && c.carrier_cost
                          ? <span className="arc-subtle" title="Your carrier’s charge, from your rate sheet. Not charged by us.">{formatMoney(c.carrier_cost, c.currency)}</span>
                          : formatMoney(c.sell_amount, c.currency)}
                      </td>
                      <td title={!c.has_recording ? undefined
                        : c.recording_status === 'saving' ? 'Recorded — still being saved to storage'
                        : c.recording_status === 'unavailable' ? 'Recorded — the file is not in storage'
                        : 'Recorded — open the call to play it'}>
                        {/* A recorder's lamp, not a play button (owner, 6 October 2026): the row
                            says the call was recorded, and the call is where it is played. */}
                        {c.has_recording && (
                          <span className="arc-rec-dot" data-state={c.recording_status ?? 'ready'}>
                            <span className="arc-sr-only">Recorded</span>
                          </span>
                        )}
                      </td>
                      {/* The call to work on (3 October 2026): outcome, tags, notes, transcript. */}
                      <td className="arc-td-right">
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-sm"
                          title="Notes, tags and outcome" aria-label="Notes, tags and outcome"
                          onClick={(e) => { e.stopPropagation(); setWork(c.id); }}>
                          <Icon name="edit" size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        {!calls.isLoading && rows.length > 0 && (
          <div className="arc-row arc-list-foot">
            <span className="arc-subtle arc-num">{rows.length} {rows.length === 1 ? 'call' : 'calls'}{calls.hasNextPage ? ' so far' : ''}</span>
            <div className="arc-spacer" />
            {calls.hasNextPage && (
              <button type="button" className="arc-btn arc-btn-sm" disabled={calls.isFetchingNextPage}
                onClick={() => void calls.fetchNextPage()}>
                {calls.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        )}

        {!win && (
          <Empty icon="clock" title="Choose two dates" hint="The second date has to be on or after the first." />
        )}
        {win && calls.data !== undefined && rows.length === 0 && (
          narrowed
            ? <Empty icon="search" title="No calls match" hint="Try a longer period, or clear the filters."
                action={<button type="button" className="arc-btn arc-btn-sm" onClick={clear}>Clear filters</button>} />
            : <Empty icon="phone" title="No calls in this period"
                hint="Calls appear here a few seconds after they end." />
        )}
      </div>

      {open && <CallModal tenantId={tenantId} callId={open} tz={tz} onClose={() => setOpen(null)} />}
      {work && <CallWorkModal tenantId={tenantId} callId={work} tz={tz} onClose={() => setWork(null)} />}
    </>
  );
}

/** MOS with a label as well as a colour, so it does not rely on hue alone. */
function MosIndicator({ mos }: { mos: string }) {
  const v = Number(mos);
  const tone = v >= 4.0 ? 'ok' : v >= 3.5 ? 'warn' : 'danger';
  const label = v >= 4.0 ? 'good' : v >= 3.5 ? 'fair' : 'poor';
  // Good is what every call should be: said plainly, so the fair and poor ones stand out.
  if (tone === 'ok') return null;
  return (
    <Badge tone={tone}>
      <span className="arc-dot" aria-hidden="true" />
      {mos} {label}
    </Badge>
  );
}
